import { useState } from 'react'
import { useLocation } from 'react-router-dom'
import Link from './LocalizedLink'
import { Menu, X, Download } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { useLanguage, stripLang } from '../contexts/LanguageContext'

const translations = {
  el: {
    nav: [
      { name: 'Αρχική', path: '/' },
      { name: 'Σχετικά', path: '/about' },
      { name: 'Λειτουργίες', path: '/features' },
      { name: 'Τιμολόγηση', path: '/pricing' },
      { name: 'Υποστήριξη', path: '/support' },
      { name: 'Επικοινωνία', path: '/contact' },
      { name: 'Μέλη', path: 'https://automechlabs.com/customer-portal/', external: true },
    ],
    download: 'Λήψη',
  },
  en: {
    nav: [
      { name: 'Home', path: '/' },
      { name: 'About', path: '/about' },
      { name: 'Features', path: '/features' },
      { name: 'Pricing', path: '/pricing' },
      { name: 'Support', path: '/support' },
      { name: 'Contact', path: '/contact' },
      { name: 'Members', path: 'https://automechlabs.com/customer-portal/', external: true },
    ],
    download: 'Download',
  },
}

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false)
  const location = useLocation()
  const { lang, setLang } = useLanguage()
  const t = translations[lang]

  const isActive = (path) => stripLang(location.pathname) === path

  return (
    <nav className="bg-white shadow-md sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center group">
            <img
              src={`${import.meta.env.BASE_URL}images/logo-navbar.png`}
              alt="AutoMechLabs Workshop Assistant"
              className="h-14 w-auto group-hover:scale-105 transition-transform duration-200"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-1">
            {t.nav.map((item) =>
              item.external ? (
                <a
                  key={item.path}
                  href={item.path}
                  className="px-4 py-2 rounded-lg font-medium transition-colors duration-200 text-gray-700 hover:text-primary-600 hover:bg-gray-100"
                >
                  {item.name}
                </a>
              ) : (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors duration-200 ${
                    isActive(item.path)
                      ? 'text-primary-600 bg-primary-50'
                      : 'text-gray-700 hover:text-primary-600 hover:bg-gray-100'
                  }`}
                >
                  {item.name}
                </Link>
              )
            )}
          </div>

          {/* CTA + Language Toggle */}
          <div className="hidden md:flex items-center space-x-3">
            <button
              onClick={() => setLang(lang === 'el' ? 'en' : 'el')}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 hover:border-gray-300 transition-colors duration-200"
              title={lang === 'el' ? 'Switch to English' : 'Εναλλαγή σε Ελληνικά'}
            >
              {lang === 'el' ? '🇬🇧 EN' : '🇬🇷 ΕΛ'}
            </button>
            <Link to="/download" className="flex items-center space-x-2 btn-primary">
              <Download className="h-4 w-4" />
              <span>{t.download}</span>
            </Link>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden p-2 rounded-lg text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="md:hidden bg-white border-t border-gray-200"
          >
            <div className="px-4 py-4 space-y-2">
              {t.nav.map((item) =>
                item.external ? (
                  <a
                    key={item.path}
                    href={item.path}
                    onClick={() => setIsOpen(false)}
                    className="block px-4 py-3 rounded-lg font-medium transition-colors duration-200 text-gray-700 hover:bg-gray-100"
                  >
                    {item.name}
                  </a>
                ) : (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setIsOpen(false)}
                    className={`block px-4 py-3 rounded-lg font-medium transition-colors duration-200 ${
                      isActive(item.path)
                        ? 'text-primary-600 bg-primary-50'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    {item.name}
                  </Link>
                )
              )}
              <div className="flex gap-3 mt-4">
                <button
                  onClick={() => { setLang(lang === 'el' ? 'en' : 'el'); setIsOpen(false) }}
                  className="flex-1 flex items-center justify-center gap-1.5 px-3 py-3 rounded-lg border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50"
                >
                  {lang === 'el' ? '🇬🇧 EN' : '🇬🇷 ΕΛ'}
                </button>
                <Link
                  to="/download"
                  onClick={() => setIsOpen(false)}
                  className="flex-1 flex items-center justify-center space-x-2 btn-primary"
                >
                  <Download className="h-4 w-4" />
                  <span>{t.download}</span>
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  )
}

export default Navbar
